import { useCallback, useEffect, useState } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { VisioRole } from '../video/video-service';

export interface WaitingParticipant {
  userId: string;
  displayName: string;
  role: VisioRole;
  joinedAt: string;
}

interface RawRow {
  room_id: string;
  user_id: string;
  display_name: string | null;
  role: VisioRole;
  status: string;
  joined_at: string;
}

function fromRow(row: RawRow): WaitingParticipant {
  return {
    userId: row.user_id,
    displayName: row.display_name || 'Invité',
    role: row.role,
    joinedAt: row.joined_at,
  };
}

export interface UseWaitingParticipantsResult {
  waiting: WaitingParticipant[];
  admit: (userId: string) => Promise<void>;
  deny: (userId: string) => Promise<void>;
  error: string | null;
}

/**
 * Salle d'attente d'une salle vidéo (Phase D, migration 0259 — invités
 * anonymes) : liste en DIRECT des `video_room_participants.status='waiting'`,
 * livrée par `postgres_changes` (calque `SupabaseMessagingService.onMessage` —
 * Postgres = source de vérité, pas de canal broadcast séparé). **Host-only** :
 * un non-hôte n'a aucune raison de voir qui attend (et ne peut de toute façon
 * pas agir — `admit_visio_participant`/`deny_visio_participant` revérifient
 * `is_visio_room_host` côté serveur). Cette liste est INDÉPENDANTE de la
 * présence WebRTC (`room.participants`, alimentée par la signalisation) : un
 * participant en attente n'a jamais rejoint le canal média.
 */
export function useWaitingParticipants(
  db: SupabaseClient,
  roomId: string,
  isHost: boolean,
): UseWaitingParticipantsResult {
  const [waiting, setWaiting] = useState<WaitingParticipant[]>([]);
  const [error, setError] = useState<string | null>(null);

  const upsert = useCallback((row: RawRow) => {
    setWaiting((prev) => {
      const rest = prev.filter((p) => p.userId !== row.user_id);
      if (row.status !== 'waiting') return rest;
      return [...rest, fromRow(row)].sort((a, b) => a.joinedAt.localeCompare(b.joinedAt));
    });
  }, []);

  useEffect(() => {
    if (!isHost) {
      setWaiting([]);
      return;
    }
    let cancelled = false;

    void (async () => {
      const { data, error: fetchError } = await db
        .from('video_room_participants')
        .select('user_id, display_name, role, status, joined_at')
        .eq('room_id', roomId)
        .eq('status', 'waiting')
        .order('joined_at', { ascending: true });
      if (cancelled) return;
      if (fetchError) {
        setError(fetchError.message);
        return;
      }
      setWaiting(((data ?? []) as RawRow[]).map(fromRow));
    })();

    const channel = db
      .channel(`visio-room-waiting:${roomId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'video_room_participants',
          filter: `room_id=eq.${roomId}`,
        },
        (payload: { new: RawRow }) => upsert(payload.new),
      )
      // UPDATE sans filtre serveur (identité de réplica par défaut, pas fiable
      // sur une colonne non-PK) — filtré côté client, comme le chat.
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'video_room_participants' },
        (payload: { new: RawRow }) => {
          if (payload.new.room_id === roomId) upsert(payload.new);
        },
      )
      .subscribe();

    return () => {
      cancelled = true;
      void channel.unsubscribe();
    };
  }, [db, roomId, isHost, upsert]);

  const admit = useCallback(
    async (userId: string) => {
      setError(null);
      const { error: rpcError } = await db.rpc('admit_visio_participant', {
        p_room_id: roomId,
        p_user_id: userId,
      });
      if (rpcError) {
        setError(rpcError.message);
        throw new Error(rpcError.message);
      }
      setWaiting((prev) => prev.filter((p) => p.userId !== userId));
    },
    [db, roomId],
  );

  const deny = useCallback(
    async (userId: string) => {
      setError(null);
      const { error: rpcError } = await db.rpc('deny_visio_participant', {
        p_room_id: roomId,
        p_user_id: userId,
      });
      if (rpcError) {
        setError(rpcError.message);
        throw new Error(rpcError.message);
      }
      setWaiting((prev) => prev.filter((p) => p.userId !== userId));
    },
    [db, roomId],
  );

  return { waiting, admit, deny, error };
}
